import { Box, useTheme } from "@mui/material";
import { useEffect, useMemo } from "react";
import { tokens } from "../../../../../../../theme";
import Text from "../../../../../../../components/theme/text/Text";
import { useTranslation } from "react-i18next";
import Subheading from "../../../../../../../components/theme/text/Subheading";
import NumberSetting from "../../../../../../../components/global/Settings/NumberSetting";
import DropdownSetting from "../../../../../../../components/global/Settings/DropdownSetting";
import BoolSetting from "../../../../../../../components/global/Settings/BoolSetting";
import SettingsSaveButton from "../../../../../../../components/global/Settings/SettingsSaveButton";
import useManualSettingsSave from "../../../../../../../components/global/Settings/useManualSettingsSave";
import { useGetSettingsQuery } from "../../../../../../../newapi/global/generalApiSlice";
import { countriesForDropdown } from "../../../../../../../utils/countries";

// The settings this tab owns, in the order they are shown. Every one of them is a day count
// that cancels orders once exceeded, so none of them autosaves: a half-typed "1" on the way
// to "14" would be enough to start cancelling backorders a day after their planned date.
// They are written together by the single Save button in the header.
const settingKeys = [
  "order_auto_cancel_backorder_days_bol",
  "order_auto_cancel_backorder_days_amazon",
  "order_auto_cancel_backorder_days_default",
];

// The weights behind the packaging material report. They share this tab's Save button for the
// same reason the day counts do: a half-typed number would land in the customer's next report.
const packagingSettingKeys = [
  "order_packaging_label_grams",
  "order_packaging_tape_meter_grams",
  "order_packaging_filling_plastic_grams_per_liter",
  "order_packaging_filling_cardboard_grams_per_liter",
];

const Orders = ( props ) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data: settings } = useGetSettingsQuery("order");
  const { handleChange, hasChanges, isSaving, save } = useManualSettingsSave();

  // The material a box is filled up with when the carrier itself names none.
  const fillingMaterialOptions = [
    { id: "none", value: "none", name: t("none") },
    { id: "cardboard", value: "cardboard", name: t("cardboard") },
    { id: "plastic", value: "plastic", name: t("plastic") },
  ];

  const countries = useMemo(() => countriesForDropdown(i18n.language), [i18n.language]);

  useEffect(() => {
    props.replaceView({
        ...props.config,
    },);

    return () => {};
  }, []);

  return (
    <Box
      sx={{
        height: 1,
        "@media screen and (max-width: 47.9375em)": {
          paddingTop: 0,
          paddingX: 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 1,
          gap: 2,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? colors.grey[300]
              : colors.bg["tertiary"],
          paddingX: 2,
          paddingTop: 4,
          height: 1,
          position: "relative",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          "@media screen and (max-width: 47.9375em)": {
            paddingTop: 0,
            paddingX: 0,
            background: colors.bg["tertiary"],
          },
        }}
      >
        <Box
          sx={{
            paddingX: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* The heading is the part that gives way on a phone; the save action has to stay
              reachable there, so the breakpoint sits on the text rather than on the row. */}
          <Box
            sx={{
              "@media screen and (max-width: 47.9375em)": {
                display: "none",
              },
            }}
          >
            <Subheading>{t("core_orders_title")}</Subheading>
            <Text light>{t("core_orders_subtitle")}</Text>
          </Box>
          <SettingsSaveButton onClick={save} isLoading={isSaving} disabled={!hasChanges} />
        </Box>
        <Box
          sx={{
            background:
              theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
            flex: 1,
            display:"flex",
            flexDirection:"column",
            gap:3,
            pt:3,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            "@media screen and (max-width: 48em)": {
             gap:2.5,
             overflow:"auto",
             pb:11,
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[200]
                  : colors.grey[0],
            },
          }}
        >
          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:3,
            }}
          >
            {settingKeys.map((key) => (
              <NumberSetting
                key={key}
                manualSave
                hideSaveButton
                maxWidth={240}
                settings={settings}
                skey={key}
                onChange={handleChange}
              />
            ))}
            <Subheading>{t("core_orders_packaging_title")}</Subheading>
            <Text light>{t("core_orders_packaging_subtitle")}</Text>
            {/* Off means no usage is recorded at all. The weights below only matter once it is on. */}
            <BoolSetting
              manualSave
              settings={settings}
              skey="order_packaging_usage_enabled"
              onChange={handleChange}
            />
            {packagingSettingKeys.map((key) => (
              <NumberSetting
                key={key}
                manualSave
                hideSaveButton
                maxWidth={240}
                settings={settings}
                skey={key}
                onChange={handleChange}
              />
            ))}
            <DropdownSetting
              manualSave
              onChange={handleChange}
              settings={settings}
              objects={fillingMaterialOptions}
              noselection={"choose_option"}
              variant={"default"}
              skey="order_packaging_filling_material_type"
            />
            <Subheading>{t("core_orders_working_days_title")}</Subheading>
            <Text light>{t("core_orders_working_days_subtitle")}</Text>
            {/* The backend only applies the rule once at least one country is excluded below. */}
            <BoolSetting
              manualSave
              settings={settings}
              skey="order_international_working_days_only"
              onChange={handleChange}
            />
            <DropdownSetting
              manualSave
              multiple
              onChange={handleChange}
              settings={settings}
              objects={countries}
              valueKey="id"
              maxWidth={280}
              noselection={"choose"}
              searchOptions={{ enabled: true, placeholder: t("search") }}
              skey="order_international_working_days_excluded_countries"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Orders;
